import React, { useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { Button, Typography, IconButton, List, ListItem, ListItemText } from "@mui/material";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import EditAmountModal from "./EditAmountModal";
import { formatAmountUnit, formatIngredient, formatState } from "../../../../utils/recipe";
import { X as XMark } from "lucide-react";
import CustomModal from "../../../modal/CustomModal";

//Actions
import { closeEditOnHandModal } from "../../../../actions/pantry";

//Icons
import { ReactComponent as HelpIcon } from "../../../../assets/icons/svg/question-circle.svg";

//Modals
import OnHandAmounts from "../help/OnHandAmounts";

const EditOnHandModal = ({ item = {}, closeEditOnHandModal }) => {
  const initialAmounts = item?.data?.onHand?.amounts || [];
  const [amounts, setAmounts] = useState(initialAmounts);
  const [editIdx, setEditIdx] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);

  console.log(amounts);

  // Drag and drop reorder
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const reordered = Array.from(amounts);
    const [removed] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, removed);
    setAmounts(reordered);
  };

  // Edit
  const handleItemEdit = (idx) => {
    setEditIdx(idx);
    setEditOpen(true);
  };
  const handleAdd = () => {
    setEditIdx(null);
    setEditOpen(true);
  };
  const handleSaveAmount = (amt) => {
    let newAmounts = [...amounts];
    if (editIdx === null) newAmounts.push(amt);
    else newAmounts[editIdx] = amt;
    setAmounts(newAmounts);
    setEditOpen(false);
  };
  const handleListItemRemove = (idx) => {
    setAmounts(amounts.filter((_, i) => i !== idx));
  };
  const handleCancelEdit = () => setEditOpen(false);

  // Save all amounts (to be hooked up to redux/patch)
  const handleSaveAll = () => {
    closeEditOnHandModal();
  };

  return (
    <div className="w-full">
      <CustomModal open={showHelpModal} handleClose={() => setShowHelpModal(false)} type="bottom">
        <OnHandAmounts />
      </CustomModal>
      <div className="w-full p-4 bg-neutral-100 flex flex-row justify-between items-center">
        <Typography variant="h6" className="font-normal">
          On-Hand Amounts
        </Typography>
        <Button
          size="small"
          disableRipple
          className="hover:bg-transparent border-neutral-400 hover:border-neutral-500"
          variant="outlined"
          onClick={() => setShowHelpModal(true)}
        >
          <HelpIcon className="w-4 h-4 mr-1" />
          Help
        </Button>
      </div>
      <div className="p-4">
        <div className="border border-neutral-300 rounded-lg bg-white max-h-60vh overflow-y-auto">
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="ingredient-list">
              {(provided) => (
                <List ref={provided.innerRef} {...provided.droppableProps} className="p-0">
                  {amounts.map((item, idx) => (
                    <Draggable key={idx} draggableId={String(idx)} index={idx}>
                      {(provided, snapshot) => (
                        <ListItem
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          style={{
                            ...provided.draggableProps.style,
                            left: "auto !important",
                            top: "auto !important",
                          }}
                          button
                          onClick={() => handleItemEdit(idx)}
                          secondaryAction={
                            <IconButton
                              edge="end"
                              aria-label="delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleListItemRemove(idx);
                              }}
                            >
                              <XMark size={20} />
                            </IconButton>
                          }
                          className={`rounded-md shadow-sm border border-neutral-200 ${
                            snapshot.isDragging ? "bg-neutral-100" : "bg-white"
                          }`}
                        >
                          <ListItemText
                            primary={(() => {
                              const displayIngredient = {
                                ...item.ingredient,
                                name:
                                  item?.ingredient?.names?.find((name) => name.default)?.value || "Unknown Ingredient",
                                amount: item.amount,
                                unit: item.unit?.display
                                  ? item.unit
                                  : {
                                      display: {
                                        magnitude: item.amount,
                                        text: item.unit?.text || item.unit?.name || "",
                                      },
                                    },
                                state: item.state,
                              };
                              return (
                                <>
                                  <span className="font-normal">{formatAmountUnit(displayIngredient)}</span>{" "}
                                  <span className="font-medium">{formatIngredient(displayIngredient)}</span>
                                  {item.state && item.state !== "" && (
                                    <span className="font-normal italic text-secondaryText text-sm">
                                      {formatState(displayIngredient)}
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                            secondary={
                              item.note && item.note !== "" ? (
                                <span className="text-secondaryText">({item.note})</span>
                              ) : null
                            }
                          />
                        </ListItem>
                      )}
                    </Draggable>
                  ))}
                  {provided.placeholder}
                </List>
              )}
            </Droppable>
          </DragDropContext>
        </div>
        <div className="w-full pt-4">
          <Button
            variant="contained"
            color="secondary"
            onClick={handleAdd}
            className="mb-2 text-primaryText"
            disableElevation
            disableRipple
            size="small"
            fullWidth
          >
            + Add Ingredient Option
          </Button>
        </div>
        <div className="w-full flex flex-row justify-between mb-4 mt-6">
          <Button
            variant="contained"
            color="secondary"
            onClick={closeEditOnHandModal}
            disableElevation
            disableRipple
            className="text-primaryText"
          >
            Cancel
          </Button>
          <Button variant="contained" color="primary" onClick={handleSaveAll} disableElevation disableRipple>
            Save
          </Button>
        </div>
      </div>

      <EditAmountModal
        open={editOpen}
        amount={editIdx !== null ? amounts[editIdx] : null}
        onSave={handleSaveAmount}
        onCancel={handleCancelEdit}
      />
    </div>
  );
};

EditOnHandModal.propTypes = {
  item: PropTypes.object,
  closeEditOnHandModal: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  item: state.pantry.item,
});

export default connect(mapStateToProps, { closeEditOnHandModal })(EditOnHandModal);
